'use client'

import type { CSSProperties } from 'react'
import { cx } from 'utils/cx'

import { dataTestIds } from '../../../constants/dataTestIds/dataTestIds'
import { useAnalytics } from '../../../contexts/AnalyticsProvider/AnalyticsProvider'
import type { Size } from '../../../types/FullProduct'
import styles from './SizeItem.module.scss'
import { SizeItemContent } from './SizeItemContent/SizeItemContent'
import { useSizeItemClick } from './useSizeItemClick/useSizeItemClick'

interface SizeItemProps {
	readonly itemHeight: number
	readonly size: Size
	readonly className?: string
	readonly extraInfoLayout?: 'row' | 'column'
	readonly isUnique?: boolean
	readonly isUnselectable?: boolean
	readonly withArrow?: boolean
	readonly withSheet?: boolean
	readonly showLastUnits?: boolean
	readonly showDelayed?: boolean
}

export const ITEM_SIZES = {
	SHEET: 56,
	ACCORDION: 36,
}

export function SizeItem({
	itemHeight,
	size,
	className,
	extraInfoLayout = 'column',
	isUnique,
	isUnselectable = false,
	withArrow = false,
	withSheet = false,
	showLastUnits = true,
	showDelayed = true,
}: SizeItemProps): React.ReactNode {
	const { sendShowSizeSelectorEvent, sendClickSizeEvent } = useAnalytics()
	const { available, extraInfo, lastUnits, isDelayed } = size
	const isSelectable = !isUnique || !available
	const Element = isSelectable ? 'button' : 'p'

	const parsedExtraInfo =
		extraInfoLayout === 'row' ? ` (${extraInfo})` : extraInfo

	const onSizeItemClick = useSizeItemClick({
		withSheet,
		isUnselectable,
	})

	const onElementClick = (): void => {
		if (isSelectable) {
			onSizeItemClick(size)
			sendClickSizeEvent({ lastUnits })
		}

		if (withArrow) {
			sendShowSizeSelectorEvent()
		}
	}

	return (
		<Element
			style={{ '--item-height': itemHeight } as CSSProperties}
			className={cx(
				className,
				styles.sizeItem,
				isSelectable && styles.selectable
			)}
			onClick={onElementClick}
			data-testid={
				available
					? dataTestIds['pdp.productInfo.sizeSelector.fn'](
							'sizeAvailable',
							size.id
						)
					: dataTestIds['pdp.productInfo.sizeSelector.fn'](
							'sizeUnavailable',
							size.id
						)
			}
		>
			<SizeItemContent
				size={size}
				withArrow={withArrow}
				parsedExtraInfo={parsedExtraInfo}
				extraInfoLayout={extraInfoLayout}
				showLastUnits={Boolean(showLastUnits && lastUnits)}
				showDelayed={Boolean(showDelayed && isDelayed)}
			/>
		</Element>
	)
}
